import { extractResourceTokenInners } from "@/components/global/components/tiptap/domain/formula-grammar";

import type { ResourceCategoryV2 } from "../fields/form-entity-select";
import { screenFieldBraceInnerMatchesLabel } from "./data-field-type-utils";

/** Minimal shape for resolving which `{…}` tokens in a formula correspond to a saved resource ref. */
export interface FormulaResourceLookupOption {
	label: string;
	value: string;
	resourceType: ResourceCategoryV2;
	triggerName?: string;
}

export interface FormulaVariableResourceRef {
	resourceType: ResourceCategoryV2;
	resourceIdentifier: string;
}

export interface FormulaVariableFormulaPayload {
	formula: string;
	resources: FormulaVariableResourceRef[];
}

export function emptyFormulaVariablePayload(): FormulaVariableFormulaPayload {
	return { formula: "", resources: [] };
}

/** Inner text of each complete `{inner}` token in the formula (trimmed). */
export function extractBraceTokenInners(formula: string): string[] {
	return extractResourceTokenInners(formula);
}

/** Drop resource refs whose token no longer appears in the formula (matches `{label}` or `{value}` from lookup). */
export function pruneFormulaResourcesByFormula(
	formula: string,
	resources: FormulaVariableResourceRef[],
	lookup: FormulaResourceLookupOption[],
): FormulaVariableResourceRef[] {
	if (!resources.length) {
		return resources;
	}
	const inners = extractBraceTokenInners(formula);
	const innerSet = new Set(inners);
	return resources.filter((ref) => {
		const opt = lookup.find((o) => {
			if (o.resourceType !== ref.resourceType) return false;
			if (o.value === ref.resourceIdentifier) return true;
			return (
				o.resourceType === "advancedTrigger" && o.triggerName === ref.resourceIdentifier
			);
		});
		if (opt) {
			if (opt.resourceType === "screenField") {
				return inners.some((inner) => screenFieldBraceInnerMatchesLabel(inner, opt.label));
			}
			const aliases = [opt.label, opt.value];
			if (opt.triggerName) aliases.push(opt.triggerName);
			return aliases.some((token) => innerSet.has(token));
		}
		return innerSet.has(ref.resourceIdentifier);
	});
}

export function dedupeFormulaResources(
	resources: FormulaVariableResourceRef[],
): FormulaVariableResourceRef[] {
	const seen = new Set<string>();
	const out: FormulaVariableResourceRef[] = [];
	for (const r of resources) {
		const key = `${r.resourceType}:${r.resourceIdentifier}`;
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(r);
	}
	return out;
}

export function normalizeVariableFormulaFromApi(raw: unknown): FormulaVariableFormulaPayload {
	if (raw == null) {
		return emptyFormulaVariablePayload();
	}
	if (typeof raw === "string") {
		return { formula: raw, resources: [] };
	}
	if (typeof raw === "object" && raw !== null && "formula" in raw) {
		const o = raw as Record<string, unknown>;
		const formula = typeof o.formula === "string" ? o.formula : "";
		const rawResources = Array.isArray(o.resources) ? o.resources : [];
		const resources: FormulaVariableResourceRef[] = [];
		for (const item of rawResources) {
			if (!item || typeof item !== "object") continue;
			const it = item as Record<string, unknown>;
			if (typeof it.resourceType !== "string" || typeof it.resourceIdentifier !== "string") {
				continue;
			}
			resources.push({
				resourceType: it.resourceType as ResourceCategoryV2,
				resourceIdentifier: it.resourceIdentifier,
			});
		}
		return { formula, resources: dedupeFormulaResources(resources) };
	}
	return emptyFormulaVariablePayload();
}
